// KnowledgeData.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import Modal from "react-modal";
import backendURL from "../../config";
import EditKnowledgeData from "./EditKnowledgeData"; // Import EditKnowledgeData
import { Modal as MuiModal, Box, Tabs, Tab, Button as MuiButton } from '@mui/material'; 

// Expanded Component for expandable rows
const ExpandedComponent = ({ data }) => {
    return (
        <div style={{ padding: "10px" }}>
            {data.type === "website_link" && (
                <>
                    <p>
                        <strong>Website Link:</strong> {data.website_link}
                    </p>
                    <p>
                        <strong>Page Content:</strong> {data.page_content}
                    </p>
                </>
            )}
            {data.type === "document" && (
                <>
                    <p>
                        <strong>Filename:</strong> {data.filename}
                    </p>
                    <p>
                        <strong>Page Content:</strong> {data.page_content}
                    </p>
                </>
            )}
            {data.type === "image" && (
                <>
                    <p>
                        <strong>Filename:</strong> {data.filename}
                    </p>
                    {data.image_content && (
                        <p>
                            <strong>Image Content:</strong> {data.image_content}
                        </p>
                    )}
                    {data.content && (
                        <p>
                            <strong>Image Content:</strong> {data.content}
                        </p>
                    )}
                </>
            )}
            {data.type === "voice" && (
                <>
                    <p>
                        <strong>Filename:</strong> {data.filename}
                    </p>
                    <p>
                        <strong>Content:</strong> {data.content}
                    </p>
                </>
            )}
        </div>
    );
};

export default function KnowledgeData({ refresh }) {
    const [combinedData, setCombinedData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [isEditModalOpen, setIsEditModalOpen] = useState(false); // State for edit modal
    const [currentKnowledgeItem, setCurrentKnowledgeItem] = useState(null); // State for current item to edit
    const [triggerFetch, setTriggerFetch] = useState(false); // State to trigger data refresh

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem("access_token");
                const headers = { Authorization: `Bearer ${token}` };

                const response = await axios.get(
                    `${backendURL}/get-knowleged`,
                    { headers }
                );
                const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

                // Directly use the data from the new API response
                setCombinedData(responseData);

            } catch (error) {
                console.error("Error fetching knowledge data:", error);
                setError("Network Error");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [refresh, triggerFetch]);

    const filteredData = combinedData.filter((item) => {
        const searchTextLower = searchText.toLowerCase();
        return (
            (item.type && item.type.toLowerCase().includes(searchTextLower)) ||
            (item.website_link && item.website_link.toLowerCase().includes(searchTextLower)) || // Website Link
            (item.filename && item.filename.toLowerCase().includes(searchTextLower)) || // Filename for docs, images, voice
            (item.page_content && item.page_content.toLowerCase().includes(searchTextLower)) || // Page content for website and docs
            (item.image_content && item.image_content.toLowerCase().includes(searchTextLower)) || // Image content
            (item.content && item.content.toLowerCase().includes(searchTextLower)) || // Voice content and fallback for image content
            (item.created_date && new Date(item.created_date).toLocaleDateString().includes(searchTextLower)) || // created_date for website links
            (item.upload_date && new Date(item.upload_date).toLocaleDateString().includes(searchTextLower)) // upload_date for docs, images, voice
        );
    });


    // Function to get background color based on data type
    const getDataTypeColor = (type) => {
        switch (type) {
            case "website_link":
                return "#8B0000"; // Dark red
            case "document":
                return "#006400"; // Dark green
            case "image":
                return "#A52A2A"; // Brown
            case "voice":
                return "#556B2F"; // Olive green
            default:
                return "#2F4F4F"; // Dark slate gray
        }
    };

    const handleDeleteKnowledge = async (row) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete this ${row.type} item?`);
        if (!confirmDelete) {
            return;
        }

        try {
            const token = localStorage.getItem("access_token");
            const headers = { Authorization: `Bearer ${token}` };

            await axios.delete(`${backendURL}/delete-knowleged/${row.type}/${row.id}`, { headers });
            setCombinedData(combinedData.filter(item => item.id !== row.id || item.type !== row.type)); // Optimistic update
            setError(`${row.type} item deleted successfully.`);
            setTriggerFetch(!triggerFetch); // Refresh data after deletion
        } catch (error) {
            console.error("Error deleting knowledge data:", error);
            setError(`Error deleting ${row.type} item: ${error.message}`);
        }
    };


    const handleEditKnowledge = (row) => {
        setCurrentKnowledgeItem(row);
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setCurrentKnowledgeItem(null);
    };

    const updateEditModal = () => {
        closeEditModal();
        setTriggerFetch(!triggerFetch); // Refresh data after closing edit modal
    };



    const columns = [
        {
            name: "Data",
            selector: (row) => row.website_link || row.filename || 'N/A', // Display website_link or filename based on availability
            sortable: true,
            wrap: true,
            compact: true,
            cell: row => <div style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.website_link || row.filename || 'N/A'}</div>,
        },
        {
            name: "Content",
            selector: (row) => row.page_content || row.image_content || row.content || 'N/A', // Display content based on type
            sortable: true,
            maxWidth: '315px', // Limit the width of this column
            style: {
                whiteSpace: 'nowrap', // Prevent text from wrapping
                overflow: 'hidden',
                textOverflow: 'ellipsis', // Add ellipsis if text overflows
            },
            cell: row => <div style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.page_content || row.image_content || row.content || 'N/A'}</div>,
        },
        {
            name: "Data Type",
            selector: (row) => row.type,
            sortable: true,
            width: "200px",
            cell: (row) => (
                <div
                    style={{
                        backgroundColor: getDataTypeColor(row.type),
                        color: "white",
                        padding: "5px 10px",
                        borderRadius: "5px",
                        fontWeight: "bold",
                        whiteSpace: 'nowrap', // Prevent text from wrapping
                        overflow: 'hidden',
                        textOverflow: 'ellipsis', // Add ellipsis if text overflows
                    }}
                >
                    {row.type}
                </div>
            ),
        },
        {
            name: "Date",
            selector: (row) => row.created_date || row.upload_date, // Display created_date or upload_date
            sortable: true,
            width: "150px",
            compact: true,
            cell: row => <div style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.created_date || row.upload_date}</div>,
        },
        {
            name: "Actions",
            cell: (row) => (
                <>
                    <button className="deleteIcon" onClick={() => handleDeleteKnowledge(row)}>
                        <i className="fa fa-trash-o fa-2x"></i>
                    </button>
                    <button className="EditIcon" onClick={() => handleEditKnowledge(row)}>
                        <i className="fa fa-pencil fa-2x"></i>
                    </button>
                </>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    
  
    // Custom Styles for the MUI Modal (using sx prop)
    const muiModalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: '80%', md: '900px' }, // Responsive width, md increased from 700px
        maxWidth: '90vw', // Ensures modal is not wider than 90% of viewport width
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        border: 'none', // MUI Box handles border/shadow via elevation or sx
        borderRadius: '8px',
        boxShadow: 24, // MUI elevation for shadow
        p: 3, // Padding (theme.spacing(3))
        display: 'flex',
        flexDirection: 'column',
        outline: 'none', // Remove default focus outline
    };

    const EditModalStyle = {
        content: {
            width: "85%",
            height: "85%",
            margin: "auto",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        },
        overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
    };


    return (
        <div>
            <main className="content">
                <div className="mb-3">
                    <h1 className="h3 d-inline align-middle"><strong>Knowledge </strong> Data</h1>
                </div>
                <div className="row">
                    <div className="col-12 col-lg-12 col-xxl-12 d-flex">
                        <div className="card flex-fill">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-lg-4"></div>
                                    <div className="col-lg-4"></div>
                                    <div className="col-lg-4">
                                        <input
                                            type="text"
                                            placeholder="Search data..."
                                            className="form-control mb-3"
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                {loading ? (
                                    <p>Loading...</p>
                                ) : filteredData.length === 0 ? (
                                    <p>No data available</p>
                                ) : (
                                    <DataTable
                                        columns={columns}
                                        data={filteredData}
                                        pagination
                                        responsive
                                        highlightOnHover
                                        pointerOnHover
                                        expandableRows
                                        expandableRowsComponent={ExpandedComponent}
                                        expandOnRowClicked
                                    />
                                )}
                                <br />
                                <div
                                    id="getError"
                                    className={`alert alert-warning ${error ? "" : "d-none"}`}
                                >
                                    {error}
                                </div>
                                <br />
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <MuiModal
                    open={isEditModalOpen}
                    onClose={closeEditModal} // Handles backdrop click and Escape key
                    aria-labelledby="monetize-modal-title"
                    aria-describedby="monetize-modal-description"
                    closeAfterTransition // Better for animations
                    // BackdropComponent={Backdrop} // Optional: for custom backdrop
                    // BackdropProps={{ timeout: 500 }} // Optional: for custom backdrop
                >
                <Box sx={muiModalStyle}>
                    <EditKnowledgeData knowledgeItem={currentKnowledgeItem} onClose={closeEditModal} onUpdate={updateEditModal} />
                </Box>
            </MuiModal>
        </div>
    );
}